import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormControl , FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';

import { SelectItem } from 'primeng/api';
import { MessageService } from '@shared/services/message-service';
import { Table } from 'primeng/table';

import { map, startWith, tap, BehaviorSubject, combineLatest, Observable, Subscription } from 'rxjs';

import { DateTime } from 'luxon';

import { CarrierService } from '@shared/services/carrier.service';
import { parseTime } from '@shared/helpers/parse-time';
import { AuthService } from '@shared/authentication/auth.service';
import { Carrier } from '@shared/models/carrier';
import { SiteSubinventory } from '@shared/models/subinventory';

import { OutboundLoadService } from '../../services/outbound-load.service';
import { ShiftContextService } from '../../services/shift-context.service';
import { TrailerService } from '../../services/trailer.service';

import {CreatedTrailer, Trailer, TrailerChecklistCheck, TrailerStatus} from '../../models/trailer';
import { ShiftContext } from '../../models/shift-context';
import { OutboundLoadPartialUpdate } from '../../models/outbound-load-partial-update';
import {OutboundLoad, OutboundLoadPartial} from '../../models/outbound-load';
import { OutboundLoadStatus } from '../../models/outbound-load-status';
import { SiteConfig } from '@shared/models/site-config';
import { environment } from '@environment';

@Component({
    selector: 'app-trailers',
    templateUrl: './trailers.component.html',
    styleUrls: ['./trailers.component.scss']
})
export class TrailersComponent implements OnInit, OnDestroy {
    @ViewChild('trailersTable') trailersTable: Table;

    allTrailers = new BehaviorSubject<Trailer[]>([]);

    searchForm = new FormGroup({
        scac: new FormControl<string>(''),
        trailerNumber: new FormControl<string>(''),
        trailerStatus: new FormControl<TrailerStatus>(null),
        siteSubinventoryId: new FormControl<number>(null),
        arrivedAt: new FormControl<Date>(null),
        unloadedAt: new FormControl<Date>(null),
        loadedAt: new FormControl<Date>(null),
        departedAt: new FormControl<Date>(null)
    });

    filteredTrailers = combineLatest([
        this.allTrailers,
        this.searchForm.valueChanges.pipe(startWith(this.searchForm.value))
    ]).pipe(map(([trailers, searchValues]) =>
        trailers.filter(t => (!searchValues.scac || t.carrier?.carrierCode.toLowerCase().includes(searchValues.scac.toLowerCase()))
            && (!searchValues.trailerNumber || t.trailerNumber.toLowerCase().includes(searchValues.trailerNumber.toLowerCase()))
            && (!searchValues.trailerStatus || t.trailerStatus === searchValues.trailerStatus)
            && (!searchValues.siteSubinventoryId || t.siteSubinventoryId === searchValues.siteSubinventoryId)
            && (!searchValues.arrivedAt || (DateTime.isDateTime(t.arrivedAt) && DateTime.fromJSDate(searchValues.arrivedAt).hasSame(t.arrivedAt, 'day')))
            && (!searchValues.unloadedAt || (DateTime.isDateTime(t.unloadedAt) && DateTime.fromJSDate(searchValues.unloadedAt).hasSame(t.unloadedAt, 'day')))
            && (!searchValues.loadedAt || (DateTime.isDateTime(t.loadedAt) && DateTime.fromJSDate(searchValues.loadedAt).hasSame(t.loadedAt, 'day')))
            && (!searchValues.departedAt || (DateTime.isDateTime(t.departedAt) && DateTime.fromJSDate(searchValues.departedAt).hasSame(t.departedAt, 'day'))))));

    showTrailerUpdateDialog: boolean;

    trailerStatusArray = [
        {value: TrailerStatus.Empty, label: 'Empty'},
        {value: TrailerStatus.Planned, label: 'Planned'},
        {value: TrailerStatus.ArrivedInbound, label: 'Arrived Inbound'},
        {value: TrailerStatus.LoadedOutbound, label: 'Loaded Outbound'},
        {value: TrailerStatus.Left, label: 'Left'}
    ];

    trailerTypeOptions: SelectItem<boolean>[] = [
        {label: 'Inbound', value: true},
        {label: 'Empty', value: false}
    ]

    loading = false;
    loadingAllChecks = false;
    loadingChecks = new BehaviorSubject<Record<Trailer["id"], boolean>>({});
    checks = new BehaviorSubject<Record<Trailer["id"], TrailerChecklistCheck>>({});
    latestChecklistCheck: TrailerChecklistCheck | null = null;

    trailerForm = new FormGroup({
        siteSubinventoryId: new FormControl<number>(null, Validators.required),
        carrierId: new FormControl<number>(null, Validators.required),
        trailerNumber: new FormControl<string>(null, Validators.required),
        arrivedAtDate: new FormControl<Date>(null),
        arrivedAtTime: new FormControl<string>(null),
        isInbound: new FormControl<boolean>(false),
    });

    showShipLoad: boolean = false;
    private subscriptions: Subscription[] = [];
    private context = new BehaviorSubject<ShiftContext>(null);
    date: DateTime;
    subinventories: SiteSubinventory[];
    selectedTrailer: Trailer | null;

    departForm = new FormGroup({
        departDate: new FormControl<Date>(null, Validators.required),
        departTime: new FormControl<string>(null, Validators.required)
    });

    updating: number | null = null;
    working: boolean;
    carrierSelects: SelectItem[] = [];
    carriers: Carrier[] = [];
    displayCarrierDialog: boolean;
    protected readonly TrailerStatus = TrailerStatus;
    loadToShip: OutboundLoad;
    siteTimezone: string = environment.fallbackTimeZone;

    isAdmin: Observable<boolean> = this.authService.isUserInAnyRoles([ 'it', 'developers', 'scs admin' ]);

    constructor(private trailerService: TrailerService,
                private shiftContextService: ShiftContextService,
                private carrierService: CarrierService,
                private messageService: MessageService,
                private outboundLoadService: OutboundLoadService,
                private router: Router,
                private authService: AuthService
    ) {
    }

    ngOnInit(): void {

        this.subscriptions = [
            this.shiftContextService.CurrentContext.subscribe(context => {
                this.context.next(context);
                this.date = context.date;
                this.subinventories = context?.site.subinventories;
                this.refreshGrid();
            }),
            this.carrierService.getCarriers().pipe(tap(carriers => {
                this.setCarriers(carriers);
            })).subscribe(),

            this.filteredTrailers.subscribe(_ => {
                this.trailersTable?.reset();
            }),

            this.trailerForm.controls.isInbound.valueChanges
                .pipe(startWith(this.trailerForm.controls.isInbound.value))
                .subscribe(isInbound => {
                    if (!isInbound) {
                        this.trailerForm.controls.arrivedAtDate.setValidators(Validators.required);
                        this.trailerForm.controls.arrivedAtTime.setValidators(Validators.required);
                    }
                    else {
                        this.trailerForm.controls.arrivedAtDate.clearValidators();
                        this.trailerForm.controls.arrivedAtTime.clearValidators();
                    }

                    this.trailerForm.controls.arrivedAtDate.updateValueAndValidity();
                    this.trailerForm.controls.arrivedAtTime.updateValueAndValidity();
            }),

            this.shiftContextService.SiteConfig.subscribe(configs => {
                this.siteTimezone = configs
                    .find(c => c.configKey === SiteConfig.SITE_TIME_ZONE)
                    ?.GetValue<string>()
                    ?? environment.fallbackTimeZone;
            }),

        ];
    }

    ngOnDestroy(): void {
        this.subscriptions.forEach(s => s.unsubscribe());

        this.allTrailers.next([]);
    }

    cancel(): void {
        this.trailerForm.reset();
    }

    resetChecklist(): void {
        if (this.loadingAllChecks) {
            return;
        }
        this.loadingAllChecks = true;

        this.trailerService.resetTrailerChecklist(this.context.value.id).subscribe({
            next: () => {
                this.getChecklist();

                this.messageService.add({
                    severity: 'success',
                    summary: 'Trailer Checklist',
                    detail: `The trailer checklist for ${this.date.toFormat('MM/dd/yyyy')} has been reset.`
                });
            },
            error: _ => {
                this.messageService.add({
                    severity: 'error',
                    summary: 'Trailer Checklist',
                    detail: `The trailer checklist reset for ${this.date.toFormat('MM/dd/yyyy')} has failed.`
                });
            },
            complete: () => {
                this.loadingAllChecks = false;
            }
        });
    }

    canUpdateStatus(trailer: Trailer): boolean {
        return trailer.trailerStatus == TrailerStatus.Empty;
    }

    departTrailer(trailer: Trailer, datetime: DateTime): void {
        this.working = true;
        this.trailerService.departTrailer(trailer.id, datetime).subscribe({
            next: () => {
                this.messageService.add({
                    severity: 'success',
                    summary: 'Trailer Updated'
                });
                this.showTrailerUpdateDialog = false;
                this.selectedTrailer = null;
                this.working = false;
                this.refreshGrid();
            }, error: () => this.working = false
        });
    }

    showOutboundLink(trailer: Trailer): boolean {
        return trailer.trailerStatus === TrailerStatus.LoadedOutbound && trailer.outboundLoadId !== null;
    }

    getOutboundLoadUrl(trailer: Trailer): string {
        return `/loads/outbound/${trailer.outboundLoadId}`;
    }

    showInboundLink(trailer: Trailer): boolean {
        return trailer.trailerStatus === TrailerStatus.ArrivedInbound && trailer.inboundLoadId !== null;
    }

    getInboundLoadUrl(trailer: Trailer): string {
        return `/loads/inbound/${trailer.inboundLoadId}`;
    }

    showLoadedDialog(trailer: Trailer): boolean {
        return trailer.trailerStatus === TrailerStatus.LoadedOutbound;
    }

    unloadTrailer(trailer: Trailer): void {
        this.router.navigateByUrl(this.getInboundLoadUrl(trailer) + '?fromTrailerForm=true');
    }

    createTrailer(): void {
        if (this.trailerForm.invalid) {
            this.messageService.add({
                severity: 'warn',
                summary: 'Unable to save new trailer',
                detail: 'Please correct the listed errors.'
            });
            this.trailerForm.markAllAsTouched();
            console.log(this.trailerForm);

            return;
        }

        const created: CreatedTrailer = this.getFormData();
        const isInboundLoad = this.trailerForm.value.isInbound;

        this.trailerService.CreateTrailer(created).subscribe({
            next: trailer => {
                this.messageService.add({
                    severity: 'success',
                    summary: 'Trailer Created',
                    detail: `Trailer ${trailer.trailerNumber} has been created.`
                });
                this.refreshGrid();

                if (isInboundLoad) {
                    this.router.navigateByUrl(`/loads/inbound/new?fromTrailerForm=true&trailerId=${trailer.id}`);
                }
            },
            error: _ => {}
        });

        this.trailerForm.reset();
    }

    updateStatus(trailer: Trailer): void {
        this.showUpdateModal(trailer);
    }

    confirmTrailerUpdate(event: Event): void {
        if (!this.departForm.valid) {
            this.departForm.markAllAsTouched();
            this.messageService.add({
                severity: 'warn',
                summary: 'Unable to Update Trailer',
                detail: 'Please correct the listed errors.'
            });
            return;
        }

        let trailer = this.selectedTrailer;
        let datetime = DateTime.fromObject({
            year: this.departForm.controls.departDate.value.getFullYear(),
            month: this.departForm.controls.departDate.value.getMonth() + 1,
            day: this.departForm.controls.departDate.value.getDate(),
            ...parseTime(this.departForm.controls.departTime.value)
        })
        this.showTrailerUpdateDialog = false;

        this.departTrailer(trailer, datetime);
    }

    getSubInventoryName(subinventoryId: number): string {
        return this.subinventories.find(s => s.id === subinventoryId)?.name;
    }

    refreshGrid(): void {
        this.loading = true;
        this.loadingChecks.next({});
        this.trailerService.getTrailers({ siteId: this.context.value.site.id, date: this.context.value.date }).subscribe({
            next: data => {
                this.allTrailers.next(data);
                this.loading = false;
            },
            error: _ => {}
        });
        this.getChecklist();
    }

    private getChecklist() {
        this.loadingAllChecks = true;
        this.trailerService.getChecklist(this.context.value.id).subscribe({
            next: checks => {
                this.checks.next(
                    checks.reduce((acc, check) => ({ ...acc, [check.trailerId]: check }), {})
                );
                this.latestChecklistCheck = checks.sort((a, b) => b.lastChanged.toMillis() - a.lastChanged.toMillis())[0] ?? null;
            },
            error: _ => {
                this.messageService.add({
                    severity: 'error',
                    summary: 'Trailer Checklist',
                    detail: `Failed to load trailer checklist for ${this.date.toFormat('MM/dd/yyyy')}.`
                });
            },
            complete: () => {
                this.loadingAllChecks = false;
            }
        });
    }

    getCheckTooltipText(check: TrailerChecklistCheck | undefined): string | undefined {
        return check?.lastChangedBy && check?.lastChanged
            ? `Last updated by:\n${check.lastChangedBy}\n\n${check.lastChanged.toFormat('MM/dd/yyyy H:mm ZZZZ')}`
            : undefined;
    }

    getTrailerStatus(trailer: Trailer): string {
        return this.toReadableString(trailer.trailerStatus);
    }

    public filterAvailableStatus(trailer: Trailer): SelectItem[] {
        if (trailer?.trailerStatus === null) {
            return [];
        }
        if (trailer?.trailerStatus === TrailerStatus.Empty || trailer?.trailerStatus === TrailerStatus.Left) {
            return this.trailerStatusArray.filter(s => s.value === TrailerStatus.Empty || s.value === TrailerStatus.Left);
        }

        return this.trailerStatusArray;
    }
    showDialogToAddCarrier(): void {
        this.displayCarrierDialog = true;
    }

    carrierSelectedChange(newCarrier: Carrier): void {
        this.setCarriers([...this.carriers, newCarrier]);

        this.trailerForm.get('carrierId').setValue(newCarrier.id);
    }

    setCarriers(carriers: Carrier[]): void {
        this.carriers = carriers.slice().sort((l, r) => {
            const left = l.carrierCode === 'NOSC' || l.carrierCode === 'CUPU' ? '' : l.carrierCodeName;
            const right = r.carrierCode === 'NOSC' || r.carrierCode === 'CUPU' ? '' : r.carrierCodeName;
            return left.localeCompare(right);
        });

        if (this.carriers.length === 1)
            this.trailerForm.get('carrierId').setValue(this.carriers[0].id);
        else if (this.trailerForm.get('carrierId').value
            && !this.carriers.find(c => c.id === this.trailerForm.get('carrierId').value))
            this.trailerForm.get('carrierId').setValue(null);

        this.carrierSelects = this.carriers.map(c => ({value: c.id, label: c.carrierCodeName}));
    }

    private showUpdateModal(trailer: Trailer): void {
        this.selectedTrailer = trailer;
        this.showTrailerUpdateDialog = true;
    }

    private toReadableString(status: TrailerStatus): string {
        return this.trailerStatusArray.find(x => x.value == status)?.label ?? '<unknown>';
    }

    shipLoadDialog(trailer: Trailer) {
        this.outboundLoadService.getLoadDetail(trailer.outboundLoadId, true).subscribe(load => {
                this.loadToShip = load;
                this.showShipLoad = true;
            }
        );
    }

    loadShipped(_: OutboundLoadPartial): void {
        this.loadToShip = null;
        this.refreshGrid();
    }

    private getFormData(): CreatedTrailer {
        let arrivedDateTime = !!this.trailerForm.value.arrivedAtDate && !!this.trailerForm.value.arrivedAtTime
            ? DateTime.fromObject({
                day: this.trailerForm.value.arrivedAtDate.getDate(),
                month: this.trailerForm.value.arrivedAtDate.getMonth() + 1,
                year: this.trailerForm.value.arrivedAtDate.getFullYear(),
                ...parseTime(this.trailerForm.value.arrivedAtTime.toString())
            }).setZone(this.siteTimezone ?? environment.fallbackTimeZone, { keepLocalTime: true })
            : null;

        return {
            siteId: this.context.value.site.id,
            siteSubinventoryId: this.trailerForm.value.siteSubinventoryId,
            carrierId: this.trailerForm.value.carrierId,
            trailerNumber: this.trailerForm.value.trailerNumber,
            arrivedAt: this.trailerForm.value.isInbound ? null : arrivedDateTime
        };
    }

    deleteTrailer(trailer: Trailer): void {
        if (trailer.outboundLoadId) {
            this.messageService.add({
                severity: 'warn',
                summary: 'Unable to delete trailer',
                detail: 'Trailer has associated outbound load.'
            });
        }

        this.trailerService.deleteTrailer(trailer.id).subscribe({
            next: () => {
                this.messageService.add({
                    severity: 'success',
                    summary: 'Trailer Deleted',
                    detail: `Trailer ${trailer.trailerNumber} has been deleted.`
                });
                this.refreshGrid();
            },
            error: _ => {}
        });
    }

    handleTrailerUpdate(updatedTrailer: Trailer): void {
        const currentTrailers = this.allTrailers.value;

        this.allTrailers.next(
            currentTrailers.map(trailer => trailer.id === updatedTrailer.id ? updatedTrailer : trailer)
        );
    }

    /**
     * Marks a trailer with a checklist check for the worked shift
     */
    checkTrailer(trailer: Trailer): void {
        if(this.loadingChecks.value[trailer.id]) {
            return;
        }

        this.loadingChecks.next({ ...this.loadingChecks.value, [trailer.id]: true });

        const checked = !this.checks.value[trailer.id]?.checked;

        this.trailerService.checkTrailer(trailer.id, this.context.value.id, checked).subscribe({
            next: (check) => {
                this.checks.next(
                    {
                        ...this.checks.value,
                        [trailer.id]: check
                    }
                );
                this.latestChecklistCheck = check;
            },
            error: _ => {
                this.messageService.add({
                    severity: 'error',
                    summary: 'Trailer Check',
                    detail: `Trailer ${trailer.trailerNumber} check has failed.`
                });
            },
            complete: () => {
                this.loadingChecks.next({...this.loadingChecks.value, [trailer.id]: false });
            }
        });
    }
}
